import React from "react";

import {
  Alert,
  Button,
  Row,
  Col,
  Divider,
  Select,
  Descriptions,
  Modal,
  Typography,
} from "antd";

const { Title } = Typography;
const Option = Select.Option;

// Bron: Richtlijn Antitrombotisch beleid 2026 — Tabel 1. Periprocedureel bloedingsrisico
// https://richtlijnendatabase.nl/richtlijn/antitrombotisch_beleid/periprocedureel_beleid_bij_antistolling.html
const steps = [
  {
    index: 0,
    title: "Anesthesiologie",
    riskLow: "-",
    riskIntermediar: "-",
    riskHigh: "Epiduraal anesthesie, Spinaal anesthesie",
    alert:
      "Voor de indeling van perifere zenuw- en interventionele pijntechnieken in laag, intermediair of hoog bloedingsrisico wordt verwezen naar de richtlijn Neuraxisblokkade en antistolling (module Antistollingsmiddelen inclusief perifere zenuwblokkade en interventionele pijntechnieken).",
  },
  {
    index: 1,
    title: "Cardiologie",
    riskLow: "Hartkatheterisatie, Ablatie (hartritme)",
    riskIntermediar:
      "Pacemaker/Implanteerbare Cardioverter Defibrillator (ICD) implantatie bij hoog trombose risico (streef-INR i.o.m. cardioloog), Pericardiocentese",
    riskHigh: "-",
  },
  {
    index: 2,
    title: "Dermatologie",
    riskLow:
      "Huidbiopt, Shave excisie, Excisie, Huidtransplantatie, Kleine huidplastieken",
    riskIntermediar:
      "Mohs micrografische chirurgie, Grote huidplastieken, Nagelchirurgie",
    riskHigh: "Deroofing, Flebectomie volgens Muller",
  },
  {
    index: 3,
    title: "Gynaecologie",
    riskLow: "Diagnostische hysteroscopie, Laser vulva, Onderzoek onder narcose",
    riskIntermediar:
      "Laparotomie, Reconstructies, Bekkenbodemchirurgie, Voor- en achterwand plastiek, In vitro fertilisatie (IVF) punctie",
    riskHigh:
      "Sectio caesarea, Abortuscurettage, Partus, Debulking, Myoomenucleatie, Radicale uterusextirpatie, Exenteratie, Stadiering ovarium/endometriumcarcinoom",
  },
  {
    index: 4,
    title: "Heelkunde",
    riskLow: "Huid-/subcutane chirurgie",
    riskIntermediar:
      "Open cholecystectomie, Adrenalectomie, Mamma amputatie, Onco/trauma amputatie, Laparoscopische chirurgie (eenvoudige procedures), Hemorroïden, Verwijderen osteosynthese materiaal, Centrale lijn in chirurgische setting",
    riskHigh:
      "Vaatchirurgie, Orgaantransplantatie, Hoofd-halschirurgie, Resecties van oesofagus, maag, darm, lever, pancreas, milt, Percutane puncties of biopten, Percutane ablaties",
  },
  {
    index: 5,
    title: "Intensive Care",
    riskLow:
      "Centraal veneuze lijn (inclusief dialyse lijn), Arterielijn, Tracheostoma",
    riskIntermediar: "Thoraxdrain",
    riskHigh: "-",
  },
  {
    index: 6,
    title: "KNO",
    riskLow:
      "Paracentese/buisjes, Panendoscopie zonder biopten, Microlaryngoscopie, Coblatie conchae inferiores, Slaap endoscopie (DISE)",
    riskIntermediar:
      "Septumcorrectie, Panendoscopie met biopten, Externe rhinoplastiek, Wekedelen chirurgie/reconstructies gelaat",
    riskHigh:
      "(Adeno)tonsillectomie, Barbed reposition pharyngoplasty (BRP), Nervus hypoglossus stimulator, Neusbijholte-operaties, Hoofd-hals oncologie/weke delen chirurgie, Sanerende en reconstructieve oorchirurgie, Orbitachirurgie, Stapesoperatie, Translabyrintaire brughoek operatie, Vrije lap reconstructies, Chirurgie voor beengeleidingsimplantaten, Endonasale dacryocystorhinostomie (DCR), Reductie conchae inferiores",
  },
  {
    index: 7,
    title: "Longziekten",
    riskLow:
      "Pleurapunctie (dunne naald), Diagnostische bronchoscopie zonder biopten of brush",
    riskIntermediar:
      "Diagnostische bronchoscopie met centrale luchtwegbiopten of brush, Microbiologische bronchoalveolaire lavage (BAL)",
    riskHigh:
      "Bronchoscopie met perifere longbiopten, Ontlastende pleuradrainage, Thoraxdrain plaatsing, Medische thoracoscopie, Immunologische BAL (i.v.m. interpretatie), Therapeutische bronchoscopie (debulking, stentplaatsing), Lymfeklierpunctie (TBNA-EBUS), Cryo biopsieën (zowel centraal als perifeer), Bronchoscopische longvolume reductie, Bronchiale thermoplastiek",
  },
  {
    index: 8,
    title: "MDL/Interne geneeskunde",
    riskLow:
      "Poliepectomie (kleine poliepen, <10 mm), Diagnostische endoscopie (gastroscopie, sigmoïdoscopie, colonoscopie, ballon geassisteerde enteroscopie) inclusief biopten, ERCP met endoprothese zonder papillotomie, Endo-echoscopie (EUS) zonder interventie, Video capsule endoscopie, Stentplaatsing (zonder dilatatie), Voedingssonde inbrengen, Rubberbandligatie bij slokdarmvarices en hemorroïden, Centraal veneuze lijn in internistische setting, Beenmergaspiratie, Ascitespunctie (dunne naald)",
    riskIntermediar:
      "Buikvetbiopsie, Getunnelde en ongetunnelde centraal veneuze lijnen voor hemodialyse, Beenmergbiopsie, Ascitesdrainage",
    riskHigh:
      "Lever-, milt- en nierbiopsie, Poliepectomie (grote poliepen of colonoscopie in het kader van het bevolkingsonderzoek), Endoscopische mucosaresectie (EMR)/submucosale dissectie (ESD)/intermusculaire dissectie (EID), Endoscopische Full Thickness Resectie (EFTR), Perorale endoscopische myotomie, Submucosal tunneling endoscopische resectie (STER), Papillotomie (biliair of pancreas), Ampullectomie, Dilatatie, Percutane endoscopische gastrostomie (PEG) plaatsing, EUS met interventie (bijv. FNA en LAMS), Endoscopische coagulatie, Therapeutische enteroscopie, Barrett endotherapie (bijv. RFA), Klieven Zenker-divertikel, Vacuümtherapie",
    alert:
      "Vanwege de hoge kans op poliepectomie (>65%) is een coloscopie in het kader van het bevolkingsonderzoek te beschouwen als een therapeutische coloscopie.",
    alertSecond:
      "Er is een aparte module ontwikkeld over het beleid rondom endoscopische procedures.",
  },
  {
    index: 9,
    title: "MKA-chirurgie en tandheelkunde",
    riskLow:
      "Extractie gebitselement(en), Parodontale behandeling, Plaatsen implantaat, Biopsie, Chirurgische verwijdering gebitselement(en), Apexresectie, Sinusbodemelevatie, (Peri)implantaire chirurgie, Abces incisie, Geleidingsanesthesie, Cave: risico verhogende factoren*",
    riskIntermediar:
      "Mond(bodem) chirurgie, Osteotomie onderkaak (BSSO), Osteotomie bovenkaak (Le Fort I osteotomie), Bimaxillaire osteotomie, Corticotomie, Fracturen aangezicht (exclusief orbita), Bottransplantaties met extra oraal bot, Open kaakgewrichtschirurgie, Cranioplastiek, Kies- en tandextracties en overige chirurgische mondzorg ingrepen bij een of meer risico verhogende factoren*",
    riskHigh:
      "Orbitafracturen, Kaakreconstructie, Hoofd-hals oncologie, Weke delen chirurgie",
    alert: "*Zie Richtlijn Antitrombotica van KIMO (2025).",
  },
  {
    index: 10,
    title: "Neurochirurgie",
    riskLow: "-",
    riskIntermediar: "Carpal tunnel release, Perifere zenuwchirurgie",
    riskHigh: "Intracraniële chirurgie, Open wervelchirurgie",
  },
  {
    index: 11,
    title: "Neurologie",
    riskLow:
      "Klinisch neurofysiologisch onderzoek (KNF): naald elektromyografie (EMG), Botuline toxine injecties (BTX), Carpaal tunnel syndroom (CTS) injecties, Huidbiopsie",
    riskIntermediar: "Lumbaalpunctie, Spierbiopsie",
    riskHigh: "-",
  },
  {
    index: 12,
    title: "Oogheelkunde",
    riskLow:
      "Cataract operatie, Conjunctiva operaties, (Posterieure lamellaire) keratoplastieken, Huid (shaving/biopten etc.), Behandelingen aan punctum en canaliculi, Strabismuschirurgie, Retinachirurgie middels vitrectomie of middels extern herstel, Laserbehandelingen van cornea, lens, iris, corpus ciliaire en retina, Cryochirurgie, Subtenon en druppel anesthesie, Retrobulbaire anesthesie",
    riskIntermediar:
      "Glaucoom implant, Ooglid chirurgie dieper dan de huid, Reconstructies met name waarbij gebruik gemaakt wordt van vrije huid, tarsus of conjunctiva, Chirurgie bij anophthalmische socket",
    riskHigh:
      "Orbita operaties, Ooglidchirurgie met openen van septum, Trabeculectomie, Traanwegchirurgie tot in de neus (externe en endonasale DCR/traanwegprothesen)",
  },
  {
    index: 13,
    title: "Orthopedie",
    riskLow: "Intra-articulaire injectie",
    riskIntermediar: "Schouder-, voet-, enkel- en handchirurgie, Arthroscopie",
    riskHigh:
      "Open wervelchirurgie, Bekkenchirurgie, Knie- en heupchirurgie, Artroplastiek",
  },
  {
    index: 14,
    title: "Plastische chirurgie",
    riskLow: "Huidkankerexcisie, Handchirurgie",
    riskIntermediar:
      "Alle grote reconstructies, Vaatmalformaties, Weke delen chirurgie in het gelaat, Mammachirurgie",
    riskHigh: "-",
  },
  {
    index: 15,
    title: "Radiologie",
    riskLow:
      "Intraveneuze procedures, Cytologische puncties (inclusief ascites- en pleurapunctie), Histologische punctie (waarbij afdrukken goed mogelijk is), Centraal veneuze lijn (ongetunnelde), Echografische/röntgengeleide gewrichtspunctie en injectie van medicatie in het gewricht, Barbotage",
    riskIntermediar:
      "Drainage vochtcollecties en opheffen afvloedbelemmering (o.a. nefrostomie, abcesdrainage, ascitesdrainage, inclusief wissels), Dubbel-J (JJ) katheter plaatsing (antegrade), Getunnelde veneuze lijnen (PAC, Hickmann en Permacath), PEG-wissel, Ablatie (RFA, microwave, Cryo) in oppervlakkig orgaan, Shuntinterventies, Diagnostische lumbaalpunctie",
    riskHigh:
      "Intra-arteriële procedures (PTA/stentplaatsing/neuro-interventies zoals IAT en EVAR abdominaal/thoracaal), Echografische of CT-geleide diepe orgaanbiopsie (afdrukken punctieplaats niet mogelijk), nierbiopsie, leverbiopsie, miltbiopsie, botbiopsie, sommige wekedelen tumorbiopsie, RFA in diep orgaan, Galwegdrainage en -wissel, PEG-plaatsing, Transjugulaire intrahepatische portosystemische shunt (TIPS), Porta-embolisatie, Aneurysmatische botcyste (ABC) embolisatie",
  },
  {
    index: 16,
    title: "Radiotherapie",
    riskLow: "-",
    riskIntermediar: "Primaire radiotherapie met brachytherapie",
    riskHigh: "-",
  },
  {
    index: 17,
    title: "Revalidatiegeneeskunde",
    riskLow:
      "Botuline toxine injecties (BTX), Intramusculaire injecties, Fenol voor zenuwblokkade, Intra-articulaire injecties",
    riskIntermediar: "-",
    riskHigh: "-",
  },
  {
    index: 18,
    title: "Sportgeneeskunde",
    riskLow:
      "Intra-articulaire injectie, Intra compartimentele drukmeting, Arteriële punctie",
    riskIntermediar: "-",
    riskHigh: "-",
  },
  {
    index: 19,
    title: "Thoraxchirurgie",
    riskLow: "-",
    riskIntermediar: "-",
    riskHigh: "Longoperatie, Mediastinoscopie, Hartoperatie",
  },
  {
    index: 20,
    title: "Traumachirurgie",
    riskLow: "-",
    riskIntermediar: "-",
    riskHigh: "Bekkenchirurgie",
  },
  {
    index: 21,
    title: "Urologie",
    riskLow:
      "Diagnostische transurethrale endoscopie (excl. resectie of biopsie), Dubbel-J (JJ) plaatsing (retrograde), JJ-wissel, Suprapubische katheter (SPC) wissel, Nefrostomiekatheter (NSK) wissel",
    riskIntermediar:
      "Laparoscopische chirurgie (excl. partiële nefrectomie), Urethraplastiek, Transurethrale blaas- en ureterchirurgie, Transurethrale prostaat laserchirurgie en electrochirurgie, Prostaatbiopsie, Plaatsen urologisch prothesemateriaal, Peniele ingrepen (circumcisie, kromstandcorrectie), NSK-plaatsing, SPC-plaatsing",
    riskHigh:
      "Partiële nefrectomie (open/laparoscopisch), Open transabdominale chirurgie, Percutane niersteenchirurgie, Scrotale chirurgie (excl. vasectomie), Extraperitoneale nieroperatie",
  },
];

class SelectBloedRisico extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      current: 0,
      visible: false,
    };
  }

  onChange = (value) => {
    const current = value;
    this.setState({ current });
  };

  showModal = () => {
    this.setState({
      visible: true,
    });
  };

  handleOk = (e) => {
    console.log(e);
    this.setState({
      visible: false,
    });
  };

  handleCancel = (e) => {
    console.log(e);
    this.setState({
      visible: false,
    });
  };

  render() {
    const { current } = this.state;

    return (
      <>
        <Row>
          <Col xs={24}>
            <Title level={4}>Categorie:</Title>
            <Select
              size="large"
              style={{ width: "100%", marginTop: "8px" }}
              onChange={this.onChange}
              showSearch
              defaultValue={0}
              filterOption={(input, option) =>
                option.props.children
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              placeholder="Selecteer een categorie.."
            >
              {steps.map((item) => (
                <Option value={item.index} key={item.title} title={item.title}>
                  {item.title}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24}>
            <Divider />
            <Title level={4}>Risico:</Title>
            <Descriptions
              column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
              bordered
              border
              size="small"
              style={{ background: "white", marginTop: "16px" }}
            >
              <Descriptions.Item label="Laag">
                {steps[current].riskLow}
              </Descriptions.Item>
              <Descriptions.Item label="Intermediair">
                {steps[current].riskIntermediar}
              </Descriptions.Item>
              <Descriptions.Item label="Hoog">
                {steps[current].riskHigh}
              </Descriptions.Item>
            </Descriptions>
            {steps[current].alert ? (
              <Alert
                message={steps[current].alert}
                type="info"
                showIcon
                style={{ margin: "8px 0" }}
              />
            ) : null}
            {steps[current].alertSecond ? (
              <Alert
                message={steps[current].alertSecond}
                type="info"
                showIcon
                style={{ margin: "8px 0" }}
              />
            ) : null}
            <Divider />
            <Alert
              message="Deze tabel is niet volledig en risico’s kunnen op basis van de casuïstiek anders ingeschat worden en kunnen per regio verschillen. Bloedingsrisico is gedefinieerd als het product van de kans op een bloeding en het risico op schade van een eventuele bloeding."
              type="warning"
              showIcon
            />
            <p style={{ marginTop: "8px", fontSize: "12px", color: "#888" }}>
              Bron: Richtlijn Antitrombotisch beleid 2026 — Tabel 1.
              Periprocedureel bloedingsrisico (richtlijnendatabase.nl)
            </p>
          </Col>
        </Row>
      </>
    );
  }
}

export default SelectBloedRisico;